/**
 * Name and string utilities for display purposes
 */

import get from 'lodash.get';

/**
 * Extract initials from a name string (1-2 characters)
 * @param name - The full name to extract initials from
 * @returns 1-2 uppercase initials
 * @example getInitials('John Doe') => 'JD'
 * @example getInitials('Jane') => 'J'
 * @example getInitials('') => '?'
 */
export const getInitials = (name: string): string => {
  if (!name || typeof name !== 'string') {
    return '?';
  }

  const trimmed = name.trim();
  if (!trimmed) {
    return '?';
  }

  // Split by spaces and filter out empty strings
  const parts = trimmed.split(/\s+/).filter(Boolean);

  if (parts.length === 0) {
    return '?';
  }

  if (parts.length === 1) {
    // Single word: take first character
    return parts[0].charAt(0).toUpperCase();
  }

  // Multiple words: take first character of first and last word
  const first = parts[0].charAt(0).toUpperCase();
  const last = parts[parts.length - 1].charAt(0).toUpperCase();
  return `${first}${last}`;
};

/**
 * Generate a consistent color from a string (for avatar backgrounds)
 * Uses a palette of visually distinct, accessible colors
 * @param str - String to generate color from
 * @returns Hex color code
 */
export const stringToColor = (str: string): string => {
  const palette = [
    '#AFB0B3', // Gray
  ];

  if (!str || typeof str !== 'string') {
    return palette[0];
  }

  // Simple hash function
  let hash = 0;
  for (let i = 0; i < str.length; i++) {
    hash = str.charCodeAt(i) + ((hash << 5) - hash);
    hash = hash & hash; // Convert to 32-bit integer
  }

  // Use absolute value and modulo to get palette index
  const index = Math.abs(hash) % palette.length;
  return palette[index];
};

/**
 * Extract donor name from a donation object
 * Priority: businessName > firstName + lastName > donorName > email > 'Anonymous'
 * @param donation - Donation object with donor information
 * @returns Formatted donor name
 */
export const getDonorName = (donation: any): string => {
  const first = get(donation, 'donor.firstName') || '';
  const last = get(donation, 'donor.lastName') || '';
  const email = get(donation, 'donor.email') || '';
  const businessName = get(donation, 'donor.businessName') || '';
  const fallback = donation?.donorName || email || 'Anonymous';

  if (businessName) {
    return businessName;
  }

  if (first || last) {
    return `${first} ${last}`.trim();
  }

  return fallback;
};
